/**
 * PHASE 3: AI Insight Engine v2 - Insights Dashboard
 * Displays all correlations, anomalies, breakthroughs, and predictions
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useIsPremiumUser } from '@/hooks/useIsPremiumUser';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, TrendingUp, AlertTriangle, Award, Brain } from 'lucide-react';
import { format } from 'date-fns';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PageHeader } from '@/components/PageHeader';

export default function InsightsV2Page() {
  const { data: isPremium, isLoading: isPremiumLoading } = useIsPremiumUser();
  
  const { data: insights, isLoading } = useQuery({
    queryKey: ['insights-v2-all'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const [correlations, trendAlerts, anomalies, breakthroughs] = await Promise.all([
        supabase
          .from('correlation_insights_v2')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('trend_alerts_v2')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('anomaly_events')
          .select('*')
          .eq('user_id', user.id)
          .order('detected_date', { ascending: false })
          .limit(20),
        supabase
          .from('breakthrough_events')
          .select('*')
          .eq('user_id', user.id)
          .order('achievement_date', { ascending: false })
          .limit(20)
      ]);

      return {
        correlations: correlations.data || [],
        trendAlerts: trendAlerts.data || [],
        anomalies: anomalies.data || [],
        breakthroughs: breakthroughs.data || []
      };
    },
    enabled: isPremium === true // Only fetch when user is premium
  });

  // Show premium gate for non-premium users
  if (isPremiumLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="AI Insights Engine" />
        <div className="container mx-auto p-6 space-y-6 animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="AI Insights Engine" />
        <div className="container mx-auto p-6 space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <Brain className="h-8 w-8 text-[#4ba2a4]" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">AI Insights Engine</h1>
              <p className="text-muted-foreground">Premium Feature</p>
            </div>
          </div>
          <Card className="p-12 text-center">
            <Brain className="h-16 w-16 text-[#4ba2a4]/40 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Premium Feature</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              The AI Insights Engine is available exclusively to Premium subscribers. 
              Upgrade your account to unlock deep intelligence, pattern detection, predictions, and breakthrough recognition.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="AI Insights Engine" />
        <div className="container mx-auto p-6 space-y-6 animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-4 bg-muted rounded w-2/3"></div>
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const hasAnyInsights = insights && (
    insights.correlations.length > 0 ||
    insights.trendAlerts.length > 0 ||
    insights.anomalies.length > 0 ||
    insights.breakthroughs.length > 0
  );

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="AI Insights Engine" />
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <Brain className="h-8 w-8 text-[#4ba2a4]" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">AI Insights Engine</h1>
            <p className="text-muted-foreground">
              Deep intelligence from your wellbeing data • Patterns • Predictions • Breakthroughs
            </p>
          </div>
        </div>

        {!hasAnyInsights ? (
          <Card className="p-12 text-center">
            <Brain className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Your Insights Will Begin Generating Soon</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Once Therappi<sup>®</sup> has enough of your daily data, we'll begin detecting patterns, correlations, 
              and opportunities for growth. Keep logging your wellbeing data and insights will appear here automatically.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              The AI Insight Engine needs at least 7-14 days of consistent data to generate meaningful insights.
            </p>
          </Card>
        ) : (
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="breakthroughs">
                Breakthroughs
                {insights && insights.breakthroughs.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{insights.breakthroughs.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="correlations">
                Patterns
                {insights && insights.correlations.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{insights.correlations.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="predictions">
                Predictions
                {insights && insights.trendAlerts.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{insights.trendAlerts.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="anomalies">
                Alerts
                {insights && insights.anomalies.filter(a => !a.acknowledged_at).length > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {insights.anomalies.filter(a => !a.acknowledged_at).length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Breakthroughs Summary */}
                {insights && insights.breakthroughs.length > 0 && (
                  <Card className="border-l-4 border-l-[#d5c5a1]">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-[#d5c5a1]" />
                        <CardTitle>Recent Breakthroughs</CardTitle>
                      </div>
                      <CardDescription>Celebrate these wins</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[200px]">
                        <div className="space-y-3">
                          {insights.breakthroughs.slice(0, 3).map((breakthrough: any) => (
                            <div key={breakthrough.id} className="p-3 bg-muted/30 rounded-lg">
                              <p className="font-medium text-sm">{breakthrough.title}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {format(new Date(breakthrough.achievement_date), 'dd MMM yyyy')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                {/* Active Alerts Summary */}
                {insights && insights.anomalies.filter(a => !a.acknowledged_at).length > 0 && (
                  <Card className="border-l-4 border-l-[#0ef1f6]">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-[#0ef1f6]" />
                        <CardTitle>Active Alerts</CardTitle>
                      </div>
                      <CardDescription>Patterns needing attention</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[200px]">
                        <div className="space-y-3">
                          {insights.anomalies.filter(a => !a.acknowledged_at).slice(0, 3).map((anomaly: any) => (
                            <div key={anomaly.id} className="p-3 bg-muted/30 rounded-lg">
                              <p className="font-medium text-sm">{anomaly.metric_name}</p>
                              <p className="text-xs text-muted-foreground mt-1">{anomaly.description}</p>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="breakthroughs" className="space-y-4">
              {insights?.breakthroughs.map((breakthrough: any) => (
                <Card key={breakthrough.id} className="border-l-4 border-l-[#d5c5a1]">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-[#d5c5a1]" />
                        <CardTitle className="text-lg">{breakthrough.title}</CardTitle>
                      </div>
                      <Badge variant="outline" className="bg-[#d5c5a1]/10 text-[#d5c5a1] border-[#d5c5a1]/20">
                        +{breakthrough.improvement_percentage?.toFixed(1)}%
                      </Badge>
                    </div>
                    <CardDescription>
                      {format(new Date(breakthrough.achievement_date), 'dd MMMM yyyy')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">{breakthrough.description}</p>
                    {breakthrough.celebration_message && (
                      <p className="text-sm text-foreground italic bg-muted/30 p-3 rounded-lg">
                        {breakthrough.celebration_message}
                      </p>
                    )}
                    {breakthrough.contributing_factors && breakthrough.contributing_factors.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {breakthrough.contributing_factors.map((factor: string, idx: number) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {factor}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="correlations" className="space-y-4">
              {insights?.correlations.map((correlation: any) => (
                <Card key={correlation.id} className="border-l-4 border-l-[#aad8d6]">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-[#aad8d6]" />
                        <CardTitle className="text-lg">
                          {correlation.metric_a.replace(/_/g, ' ')} ↔ {correlation.metric_b.replace(/_/g, ' ')}
                        </CardTitle>
                      </div>
                      <Badge variant="outline" className="bg-[#aad8d6]/10 text-[#aad8d6] border-[#aad8d6]/20">
                        {correlation.correlation_category}
                      </Badge>
                    </div>
                    <CardDescription>
                      Detected over {correlation.sample_size} days • {Math.round(correlation.confidence_score * 100)}% confidence
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{correlation.insight_text}</p>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="predictions" className="space-y-4">
              {insights?.trendAlerts.map((alert: any) => (
                <Card key={alert.id} className="border-l-4 border-l-[#4ba2a4]">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-[#4ba2a4]" />
                        <CardTitle className="text-lg">{alert.title}</CardTitle>
                      </div>
                      <Badge variant="outline" className={`
                        ${alert.severity === 'critical' ? 'bg-destructive/10 text-destructive border-destructive/20' : ''}
                        ${alert.severity === 'warning' ? 'bg-[#0ef1f6]/10 text-[#0ef1f6] border-[#0ef1f6]/20' : ''}
                        ${alert.severity === 'caution' ? 'bg-[#4ba2a4]/10 text-[#4ba2a4] border-[#4ba2a4]/20' : ''}
                        ${alert.severity === 'info' ? 'bg-muted text-muted-foreground' : ''}
                      `}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <CardDescription>
                      {alert.confidence_level && `${Math.round(alert.confidence_level * 100)}% confidence`}
                      {alert.predicted_date && ` • Predicted for ${format(new Date(alert.predicted_date), 'dd MMM yyyy')}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">{alert.description}</p>
                    {alert.actionable_advice && (
                      <div className="bg-muted/30 p-3 rounded-lg">
                        <p className="text-sm font-medium mb-1">Suggested Action:</p>
                        <p className="text-sm text-muted-foreground">{alert.actionable_advice}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="anomalies" className="space-y-4">
              {insights?.anomalies.map((anomaly: any) => (
                <Card key={anomaly.id} className="border-l-4 border-l-[#0ef1f6]">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-[#0ef1f6]" />
                        <CardTitle className="text-lg">{anomaly.metric_name}</CardTitle>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant="outline" className={`
                          ${anomaly.severity === 'severe' ? 'bg-destructive/10 text-destructive border-destructive/20' : ''}
                          ${anomaly.severity === 'significant' ? 'bg-[#0ef1f6]/10 text-[#0ef1f6] border-[#0ef1f6]/20' : ''}
                          ${anomaly.severity === 'moderate' || anomaly.severity === 'mild' ? 'bg-muted text-muted-foreground' : ''}
                        `}>
                          {anomaly.severity}
                        </Badge>
                        {anomaly.acknowledged_at && (
                          <Badge variant="secondary">Acknowledged</Badge>
                        )}
                      </div>
                    </div>
                    <CardDescription>
                      Detected on {format(new Date(anomaly.detected_date), 'dd MMMM yyyy')}
                      {anomaly.deviation_percentage && ` • ${anomaly.deviation_percentage.toFixed(1)}% deviation`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">{anomaly.description}</p>
                    {anomaly.expected_value && anomaly.actual_value && (
                      <div className="grid grid-cols-2 gap-4 p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="text-xs text-muted-foreground">Expected</p>
                          <p className="text-sm font-medium">{anomaly.expected_value.toFixed(1)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Actual</p>
                          <p className="text-sm font-medium">{anomaly.actual_value.toFixed(1)}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
